// ======================
// URL BASE
// ======================
const API_URL_TEST = 'https://prod.fielpet.com/';
const API_URL_PRODUCTION = 'https://api.fielpet.com/';
const API_URL_LOCAL_DEVICE_HOST = '192.168.68.106';

const getLocalApiUrl = (): string => {
  if (typeof window !== 'undefined') {
    const isLocalBrowser =
      window.location.protocol.startsWith('http') &&
      ['localhost', '127.0.0.1'].includes(window.location.hostname);

if (isLocalBrowser) return 'http://192.168.68.106:8000/';
  }

  return `http://${API_URL_LOCAL_DEVICE_HOST}:8000/`;
};

const API_URL_LOCAL = getLocalApiUrl();

const API_URL = {
  local: API_URL_LOCAL,
  test: API_URL_TEST,
  prod: API_URL_PRODUCTION,
} as const;

export type ApiEnvKey = keyof typeof API_URL;

const API_ENV_STORAGE_KEY = 'FP_API_ENV';

const isApiEnvKey = (value: string | null | undefined): value is ApiEnvKey =>
  Boolean(value && Object.prototype.hasOwnProperty.call(API_URL, value));

const normalizeApiEnvKey = (value: string | null | undefined): ApiEnvKey | null => {
  if (value === 'dev') return 'test';
  return isApiEnvKey(value) ? value : null;
};

const getStoredApiEnv = (): ApiEnvKey | null => {
  if (typeof window === 'undefined') return null;
  const stored = window.localStorage.getItem(API_ENV_STORAGE_KEY);
  return normalizeApiEnvKey(stored);
};

const setStoredApiEnv = (env: ApiEnvKey | null): void => {
  if (typeof window === 'undefined') return;

  if (!env) {
    window.localStorage.removeItem(API_ENV_STORAGE_KEY);
    return;
  }

  window.localStorage.setItem(API_ENV_STORAGE_KEY, env);
};

const resolveProcessApiEnv = (): ApiEnvKey => {
  const raw = process.env.REACT_APP_ENV ?? 'test';
  return normalizeApiEnvKey(raw) ?? 'test';
};

const getApiEnvKey = (): ApiEnvKey => getStoredApiEnv() ?? resolveProcessApiEnv();

const getApiBaseUrl = (): string => API_URL[getApiEnvKey()];

// ======================
// AUTENTICACIÓN
// ======================
const LOGIN = '/auth/login';
const LOGOUT = '/auth/logout';
const REFRESH = '/auth/refresh';
const REGISTER = '/auth/register';
const RECOVERYPASS = '/auth/password';

// ======================
// USUARIO / PERFIL
// ======================
const PERSONAL_DATA = '/app/user/personas';
const SOLICITUD = 'app/user/datos/update';
const UPDATE_PROFILE_PIC = '/app/user/mascotas/profilepic';
const USER_TRACKING = '/app/user/tracking';

// ======================
// MASCOTAS / PESO / ALIMENTACIÓN
// ======================
const PETS = '/app/user/mascotas';
const ADD_WEIGHT = '/app/user/mascotas/historial-peso';
const ASSOC_PETS = '/app/user/mascotas/generar-link';
const CHANGE_COVERAGE = '/app/user/cambiar-cobertura';

const ADD_FOOD = '/app/user/stock-alimento';
const REMOVE_FOOD = '/app/user/remove-stock-alimento';
const GET_FOOD_BAGS = '/app/public/alimentos';
const GET_A_FOOD = '/app/user/stock-alimento';
const GET_CONSUMO = '/app/user/stock-alimento/calcular';
const GET_FOOD_PET = '/app/user/stock-alimento/mascota';

// ======================
// WEB / PLANES / PAGOS / RAZAS / LOCALIDADES
// ======================
const GET_PLANS = '/web/planes';
const GET_PLANS_PORTAL = '/web/planes_portal';
const DATOS_PAGO = '/app/user/datospagos';
const GET_PAYMENT = '/web/mediopagos';
const GET_RAZAS = '/web/razas';
const GET_DATOS = '/web/datos';
const GET_LOCALIDADES = '/app/public/localidades';
const GET_PROVINCIA = 'web/get-provincia';

// ======================
// NOTIFICACIONES
// ======================
const NOTIFICATIONS_SUBSCRIBE = 'app/user/notificaciones/suscribir';
const NOTIFICATIONS_PENDING = '/app/user/notificaciones/pendientes';
const NOTIFICATIONS_STATUS = '/app/user/notificaciones';
const TRACKING_SESSION_SUMMARY = '/app/user/tracking/session-summary';
const TRACKING_REPORT_PROBLEM = '/app/user/tracking/report-problem';

// ======================
// MODULO SINIESTROS / VETERINARIOS / MEDICAMENTOS / DOCUMENTACIÓN
// ======================
// Veterinarios
const GET_VETERINARIO = '/app/veterinario/veterinario';
const GET_VETERINARIOS = '/app/veterinario/all-veterinarios';
const GET_VETERINARIO_VETFORM = '/app/veterinario/veterinario-vetform';
const GET_VETERINARIOS_SINIESTRO = 'app/veterinario/veterinario-siniestro';
const GET_VETERINARIOS_RECOMENDADOS = '/app/veterinario/sugeridos-sub-vet-form';

// Vet form
const VET_FORM = '/app/vet-form/submit';
const GET_VETFORM = '/app/vet-form/vet-form';
const GET_VETFORMLINK = '/app/vet-form/vet-form-link';
const ADD_VETFORM_LINK_DATA = '/app/vet-form/link';
const RE_SEND_VETFORM_LINK_DATA = '/app/vet-form/re-send-link';
const GET_INDICACIONES = '/app/vet-form/sub-vet-form';

// Siniestros
const SINIESTROS = '/app/siniestro/create-siniestro';
const GET_SINIESTRO = '/app/siniestro/siniestro';
const GET_SINIESTROS = '/app/siniestro/siniestros';
const GET_DOCUMENTACION_EXISTENTE = '/app/siniestro/documentacion';

// Comprobantes y archivos
const GET_COMPROBANTES = 'app/siniestro/get-all-comprobantes-files';
const GET_OTHER_FILES = 'app/siniestro/get-other-files';
const GET_SINIESTRO_IMAGENES = '/app/siniestro/imagenes-siniestro';
const ADD_COMPROBANTE_CLIENTE_FILE = 'app/siniestro/add-comprobante-cliente-file';
const ADD_DNI_FILE = 'app/siniestro/add-dni-file';
const ADD_LIBRETA_VACUNACION_FILE = 'app/siniestro/add-libreta-file';
const ADD_CBU_FILE = 'app/siniestro/add-cbu-file';
const ADD_EXTRA_FILE = 'app/siniestro/add-other-file';
const DOWNLOAD_POLICY = '/app/user/mascotas/descargar_poliza_mascota';
const DOWNLOAD_CARNET = '/app/user/mascotas/carnet';

// ======================
// EXPORTS
// ======================
export {
  API_URL,
  getApiBaseUrl,
  getApiEnvKey,
  getStoredApiEnv as getApiEnvOverride,
  setStoredApiEnv as setApiEnvOverride,
  // Autenticación
  LOGIN,
  LOGOUT,
  REGISTER,
  RECOVERYPASS,
  REFRESH,
  // Usuario / Perfil
  PERSONAL_DATA,
  SOLICITUD,
  UPDATE_PROFILE_PIC,
  USER_TRACKING,
  // Mascotas / Alimentación
  PETS,
  ADD_WEIGHT,
  ASSOC_PETS,
  CHANGE_COVERAGE,
  ADD_FOOD,
  REMOVE_FOOD,
  GET_FOOD_BAGS,
  GET_A_FOOD,
  GET_CONSUMO,
  GET_FOOD_PET,
  // Web / Planes / Pagos / Razas / Localidades
  GET_PLANS,
  GET_PLANS_PORTAL,
  DATOS_PAGO,
  GET_PAYMENT,
  GET_RAZAS,
  GET_DATOS,
  GET_LOCALIDADES,
  GET_PROVINCIA,

  // Notificaciones
  NOTIFICATIONS_SUBSCRIBE,
  NOTIFICATIONS_PENDING,
  NOTIFICATIONS_STATUS,
  DOWNLOAD_POLICY,
  DOWNLOAD_CARNET,
  TRACKING_SESSION_SUMMARY,
  TRACKING_REPORT_PROBLEM,
  // Modulo Siniestros
  VET_FORM,
  GET_VETFORM,
  GET_VETFORMLINK,
  ADD_VETFORM_LINK_DATA,
  RE_SEND_VETFORM_LINK_DATA,
  GET_VETERINARIO,
  GET_VETERINARIOS,
  GET_VETERINARIO_VETFORM,
  GET_VETERINARIOS_SINIESTRO,
  GET_VETERINARIOS_RECOMENDADOS,
  GET_INDICACIONES,
  SINIESTROS,
  GET_SINIESTRO,
  GET_SINIESTROS,
  GET_COMPROBANTES,
  GET_OTHER_FILES,
  GET_SINIESTRO_IMAGENES,
  ADD_COMPROBANTE_CLIENTE_FILE,
  ADD_DNI_FILE,
  ADD_LIBRETA_VACUNACION_FILE,
  ADD_CBU_FILE,
  ADD_EXTRA_FILE,
  GET_DOCUMENTACION_EXISTENTE,
};
